import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SpacingValue } from '@frk/eds-components';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Logger } from '@utils/logger';

/**
 * Logger
 */
const logger = Logger.getLogger('ContentBlocksComponent');

export interface ContentBlocksContent {
  background?: string;
  sectionId?: string;
  name?: string;
  displayName?: string;
  items?: Array<any>;
  itemsTwo?: Array<any>;
  marginBottom?: string;
  verticalAlignment?: string;
  hasContainerWide?: boolean;
}

/**
 * The Content Blocks component can be used to display a selection of
 * simple content blocks such as:
 *
 * 'Title'
 * 'Intro Paragraph'
 * 'Paragraph'
 * 'Chart'
 *
 * Selector: `ft-content-blocks`
 *
 * Exported as: `ContentBlocksComponent`
 */
@Component({
  selector: 'ft-content-blocks',
  templateUrl: './content-blocks.component.html',
  styleUrls: ['./content-blocks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentBlocksComponent
  extends AbstractBaseComponent
  implements OnChanges, OnInit {
  hasContainerWide = false;
  trackImpression = false;

  /**
   * Constructor
   */
  constructor(private route: ActivatedRoute) {
    super();
  }

  /**
   * Get content from document
   */
  get content() {
    return this.document?.getData<ContentBlocksContent>();
  }

  /**
   * Get bottom margin for the content block
   */
  get marginBottom(): SpacingValue {
    return this.document?.getData<ContentBlocksContent>()
      .marginBottom as SpacingValue;
  }

  /**
   * Read content blocks
   */
  get contentBlocks() {
    return this.content?.contentBlocks || [];
  }
  /**
   * Debug Mode toggle
   */
  debugMode: boolean;

  /**
   * On Init
   */
  ngOnInit() {
    this.route?.queryParams?.subscribe((params) => {
      logger.debug(params);
      this.debugMode = params.padding === 'debug';
      logger.debug(this.debugMode);
    });
    this.hasContainerWide = this.isContainerWide();
    this.trackImpression = this.content?.enableImpressionTracking;
  }

  /**
   * If the content block doesn't have a background add a margin
   * @param contentBlock object
   */
  getContentBlockMargin(contentBlock: any) {
    if (contentBlock.background?.toLowerCase() === 'grey') {
      return 'none';
    } else {
      return contentBlock.marginBottom || 'xl';
    }
  }

  determineSpacing(isLast?: boolean): string {
    if (isLast) {
      const marginBottom = this.component.getParameters().marginBottom;
      return marginBottom === 'auto' ? 'xl' : marginBottom;
    } else if (this.removeMarginBottom !== 'true') {
      return 'none';
    }
    return this.content?.marginBottom ? this.content.marginBottom : 'xl';
  }

  /**
   * gets container--wide setting from component
   */
  isContainerWide() {
    return this.component.getParameters()?.hasContainerWide;
  }
}
